import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Group, Icon, IconTile, Page, Ring, Row, Sheet, type IconName } from '../ui/index.js';
import StatTile from '../components/StatTile.js';
import SyncRibbon from '../components/SyncRibbon.js';
import FeelingOffModal from '../components/FeelingOffModal.js';
import { useToday } from '../hooks/useToday.js';
import { metricMeta, slotColor } from '../metrics.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { concernDuration, latestSeverity, severitySlot } from '../lib/concerns.js';
import { delta, fmtDuration, fmtNumber } from '../lib/format.js';
import { QUICK_ACTIONS, quicklogPayload, WATER_ML, type QuickActionId } from '../lib/quicklog.js';
import { buildDayTimeline, energyBalance, type TimelineEvent } from '../lib/dayTimeline.js';
import { quicklog, type Baseline } from '../api.js';
import { useUnitPref } from '../hooks/useUnitPref.js';
import { formatMetric, kgToLb, lbToKg } from '../lib/units.js';
import { deviation, deviationTone } from '../lib/baseline.js';
import InsightCards from '../components/InsightCards.js';

/** Palette slot per timeline kind, so each dot wears its domain's hue. */
const KIND_SLOT: Record<TimelineEvent['kind'], number> = {
  meal: 3,
  workout: 4,
  caffeine: 8,
  water: 2,
  work: 5,
};

/** Water has no stored target; the ring is drawn against a stated 2 L reference. */
const WATER_REF_ML = 2000;

/** Icon + tint per quick-log action (ring palette, so the page reads as one system). */
const QUICK_LOOK: Record<QuickActionId, { icon: IconName; color: string; short: string }> = {
  coffee: { icon: 'coffee', color: '#a2672f', short: 'Coffee' },
  espresso: { icon: 'coffee', color: '#8e5a2b', short: 'Espresso' },
  energy_drink: { icon: 'bolt', color: 'var(--ring-food)', short: 'Energy' },
  water: { icon: 'droplet', color: 'var(--ring-water)', short: 'Water' },
  weight: { icon: 'activity', color: 'var(--ring-sleep)', short: 'Weight' },
};

function timeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export default function Today(): JSX.Element {
  const navigate = useNavigate();
  const mode = useThemeMode();
  const unitPref = useUnitPref();
  const { data, loading, error, reload } = useToday();
  const [pending, setPending] = useState<QuickActionId | null>(null);
  const [justLogged, setJustLogged] = useState<QuickActionId | null>(null);
  const [weightOpen, setWeightOpen] = useState(false);
  const [weightInput, setWeightInput] = useState('');
  const [logError, setLogError] = useState('');
  const [concernOpen, setConcernOpen] = useState(false);

  const color = (type: string): string => slotColor(metricMeta(type).slot, mode);
  const weightUnit = unitPref === 'imperial' ? 'lb' : 'kg';

  async function runAction(id: QuickActionId, value?: number): Promise<void> {
    setLogError('');
    setPending(id);
    try {
      await quicklog(quicklogPayload(id, value));
      setJustLogged(id);
      window.setTimeout(() => setJustLogged((cur) => (cur === id ? null : cur)), 1600);
      reload();
    } catch {
      setLogError('Could not save that entry. Try again.');
    } finally {
      setPending(null);
    }
  }

  async function submitWeight(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    const entered = Number(weightInput);
    if (!Number.isFinite(entered) || entered <= 0) {
      setLogError(`Enter a weight in ${weightUnit === 'lb' ? 'pounds' : 'kilograms'}.`);
      return;
    }
    // Storage is canonical kg; imperial input is converted before POST.
    const kg = unitPref === 'imperial' ? lbToKg(entered) : entered;
    await runAction('weight', kg);
    setWeightOpen(false);
    setWeightInput('');
  }

  const restingHr = data?.latest.resting_hr ?? null;
  const weightLatest = data?.latest.weight_kg ?? null;
  const weightPrior = data?.weightSpark[0]?.v;
  // Delta is shown in the display unit, so convert both endpoints first.
  const weightDelta =
    weightLatest && weightPrior !== undefined
      ? delta(
          formatMetric('weight_kg', weightLatest.v, unitPref).value,
          formatMetric('weight_kg', weightPrior, unitPref).value,
        )
      : null;

  // Water quick-log hint: still posts mL, but the label shows the fl oz pour.
  const waterHint =
    unitPref === 'imperial'
      ? `${fmtNumber(formatMetric('water_ml', WATER_ML, unitPref).value)} fl oz`
      : `${WATER_ML} mL`;

  const balance = data
    ? energyBalance({
        caloriesIn: data.caloriesInToday,
        active: data.activeCaloriesToday,
        basal: data.basalCaloriesToday,
      })
    : null;
  const timeline = data
    ? buildDayTimeline({
        meals: data.meals,
        workouts: data.workouts,
        caffeine: data.caffeinePoints,
        water: data.waterPoints,
        workSessions: data.workSessions,
      })
    : [];
  const proteinTarget = data?.targets.protein_g;

  // The newest open concern drives both the quick-log chip's mode and the pin.
  const activeConcern = data?.openConcerns[0] ?? null;
  const activeSeverity = activeConcern ? latestSeverity(activeConcern.notes) : null;

  const eventTime = (at: string): string => (at.length <= 10 ? '—' : timeOfDay(at));
  const signed = (n: number): string => (n > 0 ? `+${fmtNumber(n, 0)}` : fmtNumber(n, 0));

  // Baseline deviations: score today's reading against the user's own rolling
  // 30-day norm. Resting HR up = warn; weight is reported but never coloured.
  // Weight's baseline is converted to the display unit so the label reads in the
  // same units as the value (z is unaffected by the linear scaling).
  const restingBaseline = data?.baselines.resting_hr ?? null;
  const restingDeviation =
    restingHr && restingBaseline
      ? (() => {
          const d = deviation(restingHr.v, restingBaseline);
          return d.label === '—'
            ? undefined
            : { label: d.label, tone: deviationTone(d.z, 'higher-worse') };
        })()
      : undefined;

  const weightBaselineDisplay = ((): Baseline | null => {
    const b = data?.baselines.weight_kg;
    if (!b) return null;
    return unitPref === 'imperial' ? { ...b, mean: kgToLb(b.mean), sd: kgToLb(b.sd) } : b;
  })();
  const weightDeviation =
    weightLatest && weightBaselineDisplay
      ? (() => {
          const displayValue = formatMetric('weight_kg', weightLatest.v, unitPref).value;
          const d = deviation(displayValue, weightBaselineDisplay);
          return d.label === '—' ? undefined : { label: d.label, tone: deviationTone(d.z, 'neutral') };
        })()
      : undefined;

  const calTarget = data?.targets.calories;
  const kcalLeft = data && calTarget ? Math.round(calTarget - data.caloriesInToday + data.activeCaloriesToday) : null;
  const waterShown = data ? formatMetric('water_ml', data.waterToday, unitPref).value : 0;
  const waterRefShown = formatMetric('water_ml', WATER_REF_ML, unitPref).value;
  const waterUnit = unitPref === 'imperial' ? 'fl oz' : 'mL';
  const sleepSec = data?.sleep?.durationSec ?? null;
  const dateLine = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const firstLoad = loading && !data;

  const rings: { key: string; label: string; value: number; max: number; color: string; shown: string; of: string }[] = [];
  if (data && calTarget) {
    rings.push({
      key: 'food',
      label: 'Food',
      value: data.caloriesInToday,
      max: calTarget,
      color: 'var(--ring-food)',
      shown: fmtNumber(data.caloriesInToday, 0),
      of: `/ ${fmtNumber(calTarget, 0)} kcal`,
    });
  }
  if (data && proteinTarget) {
    rings.push({
      key: 'protein',
      label: 'Protein',
      value: data.proteinInToday,
      max: proteinTarget,
      color: 'var(--ring-move)',
      shown: fmtNumber(data.proteinInToday, 0),
      of: `/ ${fmtNumber(proteinTarget, 0)} g`,
    });
  }
  if (data) {
    rings.push({
      key: 'water',
      label: 'Water',
      value: data.waterToday,
      max: WATER_REF_ML,
      color: 'var(--ring-water)',
      shown: fmtNumber(waterShown, 0),
      of: `/ ${fmtNumber(waterRefShown, 0)} ${waterUnit}`,
    });
  }

  const hero = (
    <Card className="hb-today" label="Today's energy">
      <div className="hb-hero">
        <div className="hb-hero__text">
          <span className="hb-hero__label">{kcalLeft !== null ? (kcalLeft >= 0 ? 'Left to eat' : 'Over today') : 'Eaten today'}</span>
          {firstLoad ? (
            <span className="hb-skel" style={{ height: 44, width: 160 }} />
          ) : (
            <span className="hb-hero__value">
              {kcalLeft !== null ? fmtNumber(Math.abs(kcalLeft), 0) : balance ? fmtNumber(balance.in, 0) : '—'}
              <span className="hb-hero__unit">kcal</span>
            </span>
          )}
          {balance && (
            <span className="hb-hero__sub">
              {fmtNumber(balance.in, 0)} eaten · {fmtNumber(data?.activeCaloriesToday ?? 0, 0)} burned
              {calTarget ? ` · goal ${fmtNumber(calTarget, 0)}` : ''}
            </span>
          )}
        </div>
        {rings.length > 0 && (
          <div className="hb-hero__aside hb-rings" aria-hidden="true">
            {rings.map((r, i) => (
              <span key={r.key} className="hb-rings__ring" style={{ inset: i * 13 }}>
                <Ring value={r.value} max={r.max} color={r.color} size={104 - i * 26} stroke={11} label="" />
              </span>
            ))}
          </div>
        )}
      </div>
      {rings.length > 0 && (
        <ul className="hb-legend">
          {rings.map((r) => (
            <li key={r.key}>
              <span className="ui-dot" style={{ background: r.color }} aria-hidden="true" />
              <span className="hb-legend__name">{r.label}</span>
              <span className="hb-legend__val">
                {r.shown} <small>{r.of}</small>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );

  const quick = (
    <section aria-label="Quick log">
      <div className="ui-group__head">
        <h2 className="ui-group__title">Log</h2>
      </div>
      <div className="hb-quick hb-quick--health">
        {QUICK_ACTIONS.map((action) => {
          const look = QUICK_LOOK[action.id];
          const hint = action.id === 'water' ? waterHint : action.id === 'weight' ? weightUnit : action.hint;
          return (
            <button
              key={action.id}
              type="button"
              className={`hb-qbtn${justLogged === action.id ? ' hb-qbtn--done' : ''}`}
              style={{ ['--q' as string]: look.color }}
              onClick={() => (action.id === 'weight' ? setWeightOpen(true) : void runAction(action.id))}
              disabled={pending !== null}
              aria-label={`${action.label} ${hint}`}
            >
              <span className="hb-qbtn__icon">
                <Icon name={justLogged === action.id ? 'check' : look.icon} size={24} />
              </span>
              <span className="hb-qbtn__label">{look.short}</span>
              <span className="hb-qbtn__hint">{hint}</span>
            </button>
          );
        })}
        <button
          type="button"
          className="hb-qbtn"
          style={{ ['--q' as string]: 'var(--accent)' }}
          onClick={() => navigate('/nutrition?add=scan')}
          aria-label="Scan food barcode"
        >
          <span className="hb-qbtn__icon">
            <Icon name="utensils" size={24} />
          </span>
          <span className="hb-qbtn__label">Scan food</span>
          <span className="hb-qbtn__hint">barcode</span>
        </button>
        <button
          type="button"
          className="hb-qbtn"
          style={{ ['--q' as string]: 'var(--warn)' }}
          onClick={() => setConcernOpen(true)}
          aria-label={activeConcern ? `Log symptom for ${activeConcern.title}` : 'Feeling off? Note a concern'}
        >
          <span className="hb-qbtn__icon">
            <Icon name="heart" size={24} />
          </span>
          <span className="hb-qbtn__label">{activeConcern ? 'Symptom' : 'Feeling off'}</span>
          <span className="hb-qbtn__hint">{activeConcern ? 'add note' : 'note it'}</span>
        </button>
      </div>
      {logError && (
        <p className="hb-form__error" role="alert" style={{ margin: '-12px 4px 24px' }}>
          {logError}
        </p>
      )}
    </section>
  );

  const concernGroup = activeConcern && (
    <Group header="Open concern">
      <Row
        leading={
          <span
            className="hb-sevdot"
            style={{
              background: activeSeverity !== null ? slotColor(severitySlot(activeSeverity), mode) : 'var(--warn)',
            }}
          />
        }
        title={activeConcern.title}
        sub={`${concernDuration(activeConcern.openedAt, null)} open${activeSeverity !== null ? ` · severity ${activeSeverity}/5` : ''}`}
        detail="Add note"
        onClick={() => navigate('/concerns')}
        chevron
      />
    </Group>
  );

  const tiles = (
    <section aria-label="Body">
      <div className="ui-group__head">
        <h2 className="ui-group__title">Body</h2>
        <button type="button" className="ui-group__action" onClick={() => navigate('/trends')}>
          Trends
        </button>
      </div>
      <div className="hb-grid">
        <StatTile
          label="Sleep"
          accent="var(--ring-sleep)"
          value={sleepSec !== null ? fmtDuration(sleepSec) : null}
          asOf={data?.sleep?.endedAt}
          note={data?.sleep ? `Woke ${timeOfDay(data.sleep.endedAt)}` : !loading ? 'No sleep recorded' : undefined}
          loading={firstLoad}
          error={!!error}
          to="/trends?metric=sleep"
        />
        <StatTile
          label="Resting HR"
          accent={color('resting_hr')}
          value={restingHr ? Math.round(restingHr.v) : null}
          unit="bpm"
          source={restingHr?.source}
          asOf={restingHr?.t}
          spark={data?.restingSpark}
          note={!restingHr && !loading ? 'No reading yet' : undefined}
          deviation={restingDeviation}
          loading={firstLoad}
          error={!!error}
        />
        <StatTile
          label="Weight"
          accent={color('weight_kg')}
          value={weightLatest ? fmtNumber(formatMetric('weight_kg', weightLatest.v, unitPref).value) : null}
          unit={weightUnit}
          source={weightLatest?.source}
          asOf={weightLatest?.t}
          staleAfterHours={168}
          spark={data?.weightSpark}
          delta={weightDelta}
          deltaCaption="vs 7d"
          note={!weightLatest && !loading ? 'No reading yet' : undefined}
          deviation={weightDeviation}
          loading={firstLoad}
          error={!!error}
        />
        <StatTile
          label="Caffeine"
          accent={color('caffeine_mg')}
          value={data ? fmtNumber(data.caffeineToday, 0) : null}
          unit="mg"
          note="today"
          loading={firstLoad}
          error={!!error}
        />
        <StatTile
          label="Burned"
          accent="var(--ring-move)"
          value={data ? fmtNumber(data.activeCaloriesToday, 0) : null}
          unit="kcal"
          note={balance ? `net ${signed(balance.net)}` : 'active today'}
          loading={firstLoad}
          error={!!error}
        />
        <StatTile
          label="Water"
          accent="var(--ring-water)"
          value={data ? fmtNumber(waterShown, 0) : null}
          unit={waterUnit}
          note="today"
          loading={firstLoad}
          error={!!error}
        />
      </div>
    </section>
  );

  const day = (
    <Group header="Today so far">
      {timeline.map((e, i) => (
        <Row
          key={`${e.kind}-${e.at}-${i}`}
          leading={<span className="hb-tl__time num">{eventTime(e.at)}</span>}
          title={
            <span className="hb-tl__title">
              <span className="ui-dot" style={{ background: slotColor(KIND_SLOT[e.kind], mode) }} aria-hidden="true" />
              {e.title}
            </span>
          }
          sub={e.detail || undefined}
        />
      ))}
    </Group>
  );

  const links = (
    <Group header="Explore" className="hb-links">
      <Row leading={<IconTile name="trend" color="#ff375f" />} title="Trends" to="/trends" />
      <Row leading={<IconTile name="utensils" color="#ff9f0a" />} title="Food" to="/nutrition" />
      <Row leading={<IconTile name="dumbbell" color="#30b0c7" />} title="Training" to="/training" />
      <Row leading={<IconTile name="clipboard" color="#5e5ce6" />} title="Records" to="/records" />
    </Group>
  );

  return (
    <Page
      title="Health"
      sub={dateLine}
      wide
      className="hb-health"
    >
      <SyncRibbon />

      {error && (
        <div className="state state--error" role="alert" style={{ marginBottom: 24 }}>
          <div className="state__title">Couldn't load your day</div>
          <div className="state__hint">{error}</div>
          <Button variant="secondary" size="sm" style={{ marginTop: 12 }} onClick={reload}>
            Retry
          </Button>
        </div>
      )}

      <div className="hb-cols">
        <div>
          {hero}
          {quick}
          {concernGroup}
          <InsightCards limit={3} />
        </div>
        <div>
          {tiles}
          {day}
          {links}
        </div>
      </div>

      {weightOpen && (
        <Sheet
          title="Log weight"
          onClose={() => {
            setWeightOpen(false);
            setLogError('');
          }}
          action={{ label: 'Save', onClick: () => void submitWeight(), disabled: pending === 'weight' }}
        >
          <form className="hb-form" onSubmit={submitWeight}>
            <label className="hb-field">
              <span>Weight ({weightUnit})</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0"
                placeholder={weightUnit}
                value={weightInput}
                onChange={(e) => setWeightInput(e.target.value)}
                autoFocus
              />
            </label>
            {logError && (
              <p className="hb-form__error" role="alert">
                {logError}
              </p>
            )}
          </form>
        </Sheet>
      )}

      {concernOpen && (
        <FeelingOffModal
          concernId={activeConcern?.id ?? null}
          concernTitle={activeConcern?.title}
          onClose={() => setConcernOpen(false)}
          onSaved={() => {
            setConcernOpen(false);
            navigate('/concerns');
          }}
        />
      )}
    </Page>
  );
}
